// Static dependency declaration: a view colocates what it reads as a `deps(root,
// { key: many(type) | one(type) })` descriptor, decoupled from any fetch endpoint,
// subscription key or merge. `useDeps` (see useDeps.ts) binds it to concrete owner
// ids at runtime. Edges are resolved against the generated schema graph, so the
// FK wiring stays in lockstep with the models.
//
// #1707 (tracker #1677) moved `many`/`one`/`Relationship`/`Deps`/`DepsResult` into
// app-base's generic `data/deps` (parameterized by a bare entity-name union, not
// Logbook's generated `SchemaEntity`); every export below is a thin instantiation
// of its app-base counterpart at `SchemaEntity`/`LogbookSchema`, so every existing
// caller keeps its exact prior name, signature and behavior. `edgeFk` alone keeps
// a Logbook-specific body: it resolves against `logbookEdges` (`Connections/
// Connection.ts`), which already folds the generated schema graph's `kind ===
// "owner"` into the generic engine's own `canonical` flag.
import {
  deps as genericDeps,
  many,
  one,
  type Cardinality,
  type Deps as GenericDeps,
  type DepsResult as GenericDepsResult,
  type Relationship as GenericRelationship,
  type RelationshipOptions,
} from "app-base/data/deps"
import { edgeFk as genericEdgeFk } from "app-base/data/schema"
import type { SchemaEntity } from "../Types/Generated/LogbookModel/Schema"
import { logbookEdges, type LogbookSchema } from "./Connections/Connection"

export { many, one }
export type { Cardinality, RelationshipOptions }

export type Relationship<K extends SchemaEntity = SchemaEntity, C extends Cardinality = Cardinality> = GenericRelationship<K, C>;

export interface Deps<R extends Record<string, Relationship> = Record<string, Relationship>> extends GenericDeps<SchemaEntity, R> {}

// Declare a view's data dependencies, rooted at an ownership anchor entity.
export function deps<R extends Record<string, Relationship>>(root: SchemaEntity, relationships: R): Deps<R> {
  return genericDeps<SchemaEntity, R>(root, relationships);
}

// The resolved shape `useDeps` returns for a declaration: each `many` key becomes
// a sorted array of its entity, each `one` key a single entity (or undefined).
export type DepsResult<R extends Record<string, Relationship<SchemaEntity>>> = GenericDepsResult<LogbookSchema, R>;

// The FK field on `to` that points back to `from`, used to bind a relationship's
// query filter from the root's ids. Prefers the canonical owner edge, falling back
// to any edge between the two entities. Returns undefined when unrelated.
export function edgeFk(from: SchemaEntity, to: SchemaEntity): string | undefined {
  return genericEdgeFk(from, to, logbookEdges);
}
